import React from 'react';
import {
  Select,
  SelectValue,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
  Label,
  type SelectProps,
} from 'react-aria-components';
import { IoChevronDown } from "react-icons/io5";

import classes from './CustomSelect.module.scss';

export interface CustomSelectItem {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface CustomSelectProps<T extends object> extends Omit<SelectProps<T>, 'children'> {
  label?: string;
  items: CustomSelectItem[];
  placeholder?: string;
  className?: string;
  error?: string;
  helperText?: string;
  isLoading?: boolean;
  renderValue?: (item: CustomSelectItem | null) => React.ReactNode;
  renderItem?: (item: CustomSelectItem) => React.ReactNode;
}

export function CustomSelect<T extends object = CustomSelectItem>({
  label,
  items,
  placeholder = 'Select an option',
  className,
  error,
  helperText,
  isLoading,
  renderValue,
  renderItem,
  isDisabled,
  isRequired,
  ...props
}: CustomSelectProps<T>) {
  return (
    <div className={`${classes.selectWrapper} ${className || ''}`}>
      <Select
        {...props}
        isDisabled={isDisabled || isLoading}
        isRequired={isRequired}
        className={classes.select}
      >
        {label && (
          <Label className={classes.label}>
            {label}
            {isRequired && <span className={classes.required}>*</span>}
          </Label>
        )}
        
        <Button 
          className={`${classes.trigger} ${error ? classes.triggerError : ''}`}
        >
          <SelectValue className={classes.value}>
            {({ selectedItem, selectedText, isPlaceholder }) => {
              if (isPlaceholder || !selectedItem) {
                return <span className={classes.placeholder}>{placeholder}</span>;
              }
              
              // Find the actual item from our items array
              const item = items.find((i) => i.id === (selectedItem as CustomSelectItem).id);
              
              if (renderValue && item) {
                return renderValue(item);
              }
              
              return selectedText || item?.label || placeholder;
            }}
          </SelectValue>
          <span className={classes.chevronWrapper}>
            <IoChevronDown className={classes.chevron} />
          </span>
        </Button>

        <Popover className={classes.popover}>
          <ListBox className={classes.listBox} items={items}>
            {(item) => (
              <ListBoxItem
                key={item.id}
                id={item.id}
                textValue={item.label}
                isDisabled={item.disabled}
                className={classes.option}
              >
                {({ isSelected, isFocused, isPressed, isDisabled }) => (
                  <div 
                    className={`
                      ${classes.optionContent}
                      ${isSelected ? classes.optionSelected : ''}
                      ${isFocused ? classes.optionFocused : ''}
                      ${isPressed ? classes.optionPressed : ''}
                      ${isDisabled ? classes.optionDisabled : ''}
                    `}
                  >
                    {renderItem ? (
                      renderItem(item)
                    ) : (
                      <>
                        <span className={classes.optionLabel}>{item.label}</span>
                        {item.description && (
                          <span className={classes.optionDescription}>{item.description}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </Select>

      {error && <div className={classes.error}>{error}</div>}
      {helperText && !error && <div className={classes.helperText}>{helperText}</div>}
    </div>
  );
}

export default CustomSelect;